import { invalidatePolicyQueries } from '@/utils/policy-cache'
import {
  useEffect,
  useCallback,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { deserializeFromMarkdown } from '@/tiptap/markdown/deserializeFromMarkdown'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import { applyBlockToEditor } from './helpers'
import type { GenerationItem, PolicyItem } from './types'
import type { ApprovePolicyArgs } from './ApprovePolicyDialog'

// ── Shared queries ──

export function usePolicyDetailQueries(explicitFramework?: string) {
  const programQuery = useQuery(
    trpc.complianceProgram.getProgram.queryOptions()
  )
  // A policy's mapped controls span every framework it satisfies. Match the
  // Policies list (which deliberately unions all enrolled frameworks) by NOT
  // gating on the active-framework atom — only scope when a caller explicitly
  // passes one. Previously this defaulted to the active framework, so the
  // detail page hid the other framework's mapped controls (e.g. dropping the
  // SOC 2 refs while the list still showed them).
  const itemsQuery = useQuery({
    ...trpc.complianceProgram.getGenerationItems.queryOptions(
      explicitFramework ? { framework: explicitFramework } : undefined
    ),
    // Poll while any policy is drafting in the background so the detail
    // page can flip from "Queued/Generating…" placeholder to content
    // without a manual refresh.
    refetchInterval: (q) => {
      const data = q.state.data as { items?: GenerationItem[] } | undefined
      const anyInFlight = (data?.items ?? []).some(
        (i) =>
          i.generationState === 'queued' || i.generationState === 'generating',
      )
      return anyInFlight ? 4000 : false
    },
  })
  const policiesQuery = useQuery(
    trpc.complianceProgram.getPolicies.queryOptions()
  )

  // Regeneration runs in a background worker — the enqueue mutation resolves
  // before any content is written, so enqueue-time invalidations refetch the
  // OLD document. Watch the polled generationState and invalidate the
  // content-bearing queries when an item leaves the in-flight set, so the
  // open document repaints with the projected result (RFC-215 §5.7.1:
  // present "immediate" by observing projection state, not blind timers).
  const queryClient = useQueryClient()
  const inFlightDocsRef = useRef<Map<string, string | null>>(new Map())
  useEffect(() => {
    const rows = ((itemsQuery.data as any)?.items ?? []) as GenerationItem[]
    const inFlight = new Map<string, string | null>()
    for (const row of rows) {
      if (
        row.generationState === 'queued' ||
        row.generationState === 'generating'
      ) {
        inFlight.set(row.slug, row.policyDocumentId ?? null)
      }
    }
    const prev = inFlightDocsRef.current
    inFlightDocsRef.current = inFlight
    const completed = [...prev].filter(([slug]) => !inFlight.has(slug))
    if (completed.length === 0) return
    for (const [, policyDocumentId] of completed) {
      if (!policyDocumentId) continue
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicyDocContent.queryKey({
          policyDocumentId,
        }),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicyVersions.queryKey({
          policyDocumentId,
        }),
      })
    }
    queryClient.invalidateQueries({
      queryKey: trpc.complianceProgram.getPolicyCommitments.queryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.complianceProgram.getPolicyDraftClaims.queryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.complianceProgram.getPolicyClaims.queryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.claimTriage.list.queryKey(),
    })
    invalidatePolicyQueries(queryClient)
  }, [itemsQuery.data, queryClient])

  const programId = (programQuery.data as any)?.id ?? ''
  // Mirror the server's visibility rule (`visiblePolicySlugs`): drop
  // deliverables (upload asks on the test axis) but keep narratives — the
  // old `category !== 'deliverable'` filter also swallowed narratives
  // (`isms-scope`) because getGenerationItems maps every non-policy layer to
  // category 'deliverable', so /policies/isms-scope rendered "Policy not
  // found". System Description keeps its dedicated surface.
  const items: GenerationItem[] = (
    ((itemsQuery.data as any)?.items ?? []) as GenerationItem[]
  ).filter((i) => i.layer !== 'deliverable' && i.slug !== 'system-description')
  const policies: PolicyItem[] = ((policiesQuery.data as any)?.policies ??
    []) as PolicyItem[]

  return {
    programQuery,
    itemsQuery,
    policiesQuery,
    programId,
    items,
    policies,
    isLoading:
      programQuery.isLoading || itemsQuery.isLoading || policiesQuery.isLoading,
  }
}

// ── Approve mutation ──

export function useApprovePolicy({
  programId,
  policySlug,
  policyDocumentId,
  onSuccess,
}: {
  programId: string
  policySlug: string
  policyDocumentId?: string | null
  onSuccess?: () => void
}) {
  const queryClient = useQueryClient()
  const approveMutation = useMutation(
    trpc.complianceProgram.approvePolicy.mutationOptions()
  )
  const { mutateAsync: approvePolicy } = approveMutation

  const handleApprove = useCallback(
    async (args?: ApprovePolicyArgs) => {
      if (!programId || !policySlug) return
      await (approvePolicy as any)({
        programId,
        policySlug,
        override: args?.override,
        changeType: args?.changeType,
      })
      toast.success('Policy approved')
      await invalidatePolicyQueries(queryClient)
      if (policyDocumentId) {
        queryClient.invalidateQueries({
          queryKey: trpc.complianceProgram.getPolicyVersions.queryKey({
            policyDocumentId,
          }),
        })
        // Intentionally NOT invalidating getPolicyDocContent — approve
        // only flips metadata (approvalSignature), not the markdown or
        // TipTap JSON. Refetching the content would bump `dataUpdatedAt`
        // and force PolicyContentViewer to remount, which triggers a
        // TipTap/React DOM reconciliation conflict.
      }
      queryClient.invalidateQueries({
        queryKey: trpc.claimTriage.progress.queryKey({ programId }),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getReadiness.queryKey({
          programId,
        }),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.task.board.queryKey({
          programId,
        }),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getTestPlanOutline.queryKey({
          programId,
        }),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.compliance.getRecommendedEvidence.queryKey(),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.evidenceDocLink.list.queryKey(),
      })
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.getStatusById.queryKey(),
      })
      if (policyDocumentId) {
        queryClient.invalidateQueries({
          queryKey: trpc.complianceProgram.getPolicyReviewHistory.queryKey({ policyDocumentId }),
        })
      }
      onSuccess?.()
    },
    [
      approvePolicy,
      policyDocumentId,
      policySlug,
      programId,
      queryClient,
      onSuccess,
    ]
  )

  return { approveMutation, handleApprove }
}

// ── Event listeners for apply-block and policy-generated ──

/**
 * Registers event listeners for:
 * - screenata:apply-block — applies block-targeted edits from chat
 * - screenata:apply-to-editor — legacy full-doc apply fallback
 *
 * Uses applyBlockToEditor with section-aware heading expansion.
 * Temporarily enables editing if editor is read-only.
 */
/** True when a block with `blockId` exists in the editor's current doc. */
function editorHasBlock(editor: any, blockId: string): boolean {
  let found = false
  editor.state.doc.descendants((node: any) => {
    if (found) return false
    if (node.attrs?.id === blockId) {
      found = true
      return false
    }
  })
  return found
}

/**
 * Polls until the editor is editable AND contains `blockId` (the draft
 * cloned from the approved version carries the same block IDs), then
 * resolves with the editor. Resolves null on timeout. Used after branching
 * a draft, since the new draft content loads asynchronously.
 */
function waitForEditableBlock(
  editorRef: MutableRefObject<any>,
  blockId: string,
  timeoutMs: number,
): Promise<any | null> {
  return new Promise((resolve) => {
    const start = Date.now()
    const tick = () => {
      const editor = editorRef.current
      if (editor?.isEditable && editorHasBlock(editor, blockId)) {
        resolve(editor)
        return
      }
      if (Date.now() - start >= timeoutMs) {
        resolve(null)
        return
      }
      setTimeout(tick, 120)
    }
    tick()
  })
}

export function usePolicyEditorEvents(
  editorRef: MutableRefObject<any>,
  options?: {
    readOnly?: boolean
    policySlug?: string
    /** True when the editor currently shows an editable draft version. */
    isEditing?: boolean
    /**
     * Ensure the policy is on an editable draft — branching one from the
     * approved version if needed — and switch the view to it. Resolves true
     * once a draft is guaranteed. When omitted, apply mutates the current
     * editor directly (legacy behavior).
     */
    ensureEditable?: () => Promise<boolean>
  }
) {
  // Hold the latest values in refs so the window listener registers once
  // (stable across renders) yet always reads current state.
  const policySlugRef = useRef(options?.policySlug)
  const isEditingRef = useRef(options?.isEditing)
  const ensureEditableRef = useRef(options?.ensureEditable)
  policySlugRef.current = options?.policySlug
  isEditingRef.current = options?.isEditing
  ensureEditableRef.current = options?.ensureEditable

  useEffect(() => {
    // Apply a block to the (assumed-editable) editor, ack, and toast.
    const applyToEditor = (
      editor: any,
      blockId: string,
      action: string,
      content: string | undefined,
      label: string | undefined,
      /** When set, the pending toast to replace instead of stacking a new one. */
      toastId?: string | number,
    ): boolean => {
      const wasEditable = editor.isEditable
      if (!wasEditable) editor.setEditable(true)
      try {
        const result = applyBlockToEditor(editor, blockId, action, content, label)
        window.dispatchEvent(
          new CustomEvent('screenata:apply-block-ack', {
            detail: { blockId, ok: result.ok },
          }),
        )
        if (!result.ok) {
          toast.error(
            `Couldn't apply "${label ?? 'section'}". It may already be applied`,
            toastId ? { id: toastId } : undefined,
          )
        } else {
          const verb =
            action === 'delete'
              ? 'Removed'
              : action === 'insert-after'
                ? 'Inserted'
                : 'Applied'
          toast.success(
            `${verb} "${label ?? 'section'}"`,
            toastId ? { id: toastId } : undefined,
          )
        }
        return result.ok
      } finally {
        if (!wasEditable) editor.setEditable(false)
      }
    }

    // Branch an editable draft (from approved), wait for it to load, then
    // apply. Never mutates an approved version in place.
    const branchDraftAndApply = async (
      ensureEditable: () => Promise<boolean>,
      blockId: string,
      action: string,
      content: string | undefined,
      label: string | undefined,
    ) => {
      // Branching a draft is a server round-trip plus an async content
      // load — several seconds. Without a pending toast the UI looked
      // frozen and then produced a bare error, with nothing in between.
      const toastId = toast.loading(
        `Preparing "${label ?? 'section'}"…`,
      )
      let ensured = false
      try {
        ensured = await ensureEditable()
      } catch {
        ensured = false
      }
      if (!ensured) {
        toast.dismiss(toastId)
        // No toast here: every `ensureEditable` failure path already
        // explains itself (lock held by someone else, or the server's own
        // reason via the global tRPC error handler — e.g. "Policy is
        // pending approval. Return it for changes before editing."). A
        // generic message on top just stacked a third error on one cause.
        return
      }
      const editor = await waitForEditableBlock(editorRef, blockId, 6000)
      if (!editor) {
        toast.error(
          `Draft is still loading. Click Apply again to apply "${label ?? 'section'}"`,
          { id: toastId },
        )
        return
      }
      applyToEditor(editor, blockId, action, content, label, toastId)
    }

    const handler = (e: Event) => {
      const {
        blockId,
        action,
        content,
        label,
        policySlug: targetSlug,
      } = (e as CustomEvent).detail ?? {}
      // Guard: when the event names a target policy, ONLY the editor showing
      // exactly that policy may apply + acknowledge it. This stops a stale
      // editor for a different policy (e.g. a route page behind the preview
      // overlay) — or a slug-less editor — from racing to ack and mis-reporting
      // success/failure to the chat. Events without a target slug (legacy
      // inline-marker edits) still apply everywhere, as before.
      if (targetSlug && targetSlug !== policySlugRef.current) return
      if (!editorRef.current || !blockId) return

      // Approved / non-draft view: branch an editable draft first, then apply
      // to it. Ack now (ok:false) so the chat's "no editor open" fallback
      // doesn't fire while the draft is created (slower than its 300ms window).
      if (isEditingRef.current === false && ensureEditableRef.current) {
        window.dispatchEvent(
          new CustomEvent('screenata:apply-block-ack', {
            detail: { blockId, ok: false },
          }),
        )
        void branchDraftAndApply(
          ensureEditableRef.current,
          blockId,
          action,
          content,
          label,
        )
        return
      }

      applyToEditor(editorRef.current, blockId, action, content, label)
    }

    const legacyHandler = (e: Event) => {
      const content = (e as CustomEvent).detail?.content
      if (!content || !editorRef.current) return
      const editor = editorRef.current
      const wasEditable = editor.isEditable
      if (!wasEditable) editor.setEditable(true)
      try {
        const { content: parsed } = deserializeFromMarkdown(content)
        editor.chain().focus().setContent(parsed).run()
      } catch {
        try {
          editor.commands.setContent(content)
        } catch {
          /* ignore */
        }
      } finally {
        if (!wasEditable) editor.setEditable(false)
      }
    }

    window.addEventListener('screenata:apply-block', handler)
    window.addEventListener('screenata:apply-to-editor', legacyHandler)
    return () => {
      window.removeEventListener('screenata:apply-block', handler)
      window.removeEventListener('screenata:apply-to-editor', legacyHandler)
    }
  }, [editorRef])
}

/**
 * Listens for screenata:policy-generated events and invalidates relevant queries.
 */
export function usePolicyGeneratedEvent(opts: {
  programId: string
  policySlug: string
  onGenerated?: (policyDocumentId: string) => void
}) {
  const queryClient = useQueryClient()
  const { onGenerated, policySlug, programId } = opts

  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null
    let lastDocId: string | null = null

    const handler = (e: Event) => {
      const policyDocumentId = (e as CustomEvent).detail?.policyDocumentId
      if (!policyDocumentId) return

      // Debounce: multiple GeneratePolicyMessage components re-mount from
      // chat history and each dispatches this event. Collapse into one
      // invalidation round per policyDocumentId.
      if (policyDocumentId === lastDocId && debounceTimer) return
      lastDocId = policyDocumentId
      if (debounceTimer) clearTimeout(debounceTimer)

      debounceTimer = setTimeout(() => {
        debounceTimer = null
        queryClient.invalidateQueries({
          queryKey: trpc.complianceProgram.getPolicyDocContent.queryKey({
            policyDocumentId,
          }),
        })
        invalidatePolicyQueries(queryClient)
        queryClient.invalidateQueries({
          queryKey: trpc.complianceProgram.getPolicyDraftClaims.queryKey({
            programId,
            policySlug,
          }),
        })
        onGenerated?.(policyDocumentId)
      }, 200)
    }
    window.addEventListener('screenata:policy-generated', handler)
    return () => {
      window.removeEventListener('screenata:policy-generated', handler)
      if (debounceTimer) clearTimeout(debounceTimer)
    }
  }, [onGenerated, policySlug, programId, queryClient])
}

export function usePolicyDraftStreamEvent(opts: {
  setLiveDraftContent: Dispatch<SetStateAction<string | null>>
  setIsGeneratingPolicy?: Dispatch<SetStateAction<boolean>>
}) {
  const { setLiveDraftContent, setIsGeneratingPolicy } = opts

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {}
      // A `clear: true` event marks end-of-stream — drop the live preview so the editor reverts to the server-saved (post-revision) TipTap doc.
      if (detail.clear === true) {
        console.log('[usePolicyDraftStreamEvent] clear → isGeneratingPolicy=false', new Error().stack?.split('\n').slice(0, 4).join(' '))
        setIsGeneratingPolicy?.(false)
        setLiveDraftContent(null)
        return
      }
      const policyContent =
        typeof detail.policyContent === 'string' ? detail.policyContent : null
      const policyContentDelta =
        typeof detail.policyContentDelta === 'string'
          ? detail.policyContentDelta
          : null
      if (!policyContent && !policyContentDelta) return

      console.log('[usePolicyDraftStreamEvent] content → isGeneratingPolicy=true', { hasContent: !!policyContent, hasDelta: !!policyContentDelta, deltaLen: policyContentDelta?.length })
      setIsGeneratingPolicy?.(true)
      setLiveDraftContent((prev) => {
        if (policyContent && policyContent.trim().length > 0) {
          return policyContent
        }
        if (policyContentDelta && policyContentDelta.length > 0) {
          return `${prev ?? ''}${policyContentDelta}`
        }
        return prev
      })
    }

    window.addEventListener('screenata:policy-draft-stream', handler)
    return () =>
      window.removeEventListener('screenata:policy-draft-stream', handler)
  }, [setIsGeneratingPolicy, setLiveDraftContent])
}

export type PolicyStageStatus = {
  stage: string
  state?: 'start' | 'end'
  label: string
  detail?: string
} | null

export function usePolicyStageEvent(): PolicyStageStatus {
  const [stage, setStage] = useState<PolicyStageStatus>(null)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {}
      if (detail.clear === true) {
        setStage(null)
        return
      }
      if (typeof detail.label === 'string' && detail.label.length > 0) {
        setStage({
          stage: typeof detail.stage === 'string' ? detail.stage : 'unknown',
          state: detail.state === 'end' ? 'end' : 'start',
          label: detail.label,
          detail: typeof detail.detail === 'string' ? detail.detail : undefined,
        })
      }
    }
    window.addEventListener('screenata:policy-stage', handler)
    return () => window.removeEventListener('screenata:policy-stage', handler)
  }, [])
  return stage
}

export function usePolicyDraftClaimMutations(opts: {
  programId: string
  policySlug: string
}) {
  const queryClient = useQueryClient()

  const createDraftClaimMutation = useMutation(
    trpc.complianceProgram.createPolicyDraftClaim.mutationOptions()
  )
  const updateDraftClaimMutation = useMutation(
    trpc.complianceProgram.updatePolicyDraftClaim.mutationOptions()
  )
  const deleteDraftClaimMutation = useMutation(
    trpc.complianceProgram.deletePolicyDraftClaim.mutationOptions()
  )

  const invalidateDraftClaimQueries = useCallback(
    async (_policyDocumentId?: string | null) => {
      // Only invalidate claim-related queries — NOT getPolicyDocContent.
      // Invalidating the doc content causes the editor to re-render and
      // scroll to top, which is disruptive during claim CRUD operations
      // where the policy text itself hasn't changed.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: trpc.complianceProgram.getPolicyDraftClaims.queryKey({
            programId: opts.programId,
            policySlug: opts.policySlug,
          }),
        }),
        invalidatePolicyQueries(queryClient),
      ])
    },
    [opts.policySlug, opts.programId, queryClient]
  )

  return {
    createDraftClaimMutation,
    updateDraftClaimMutation,
    deleteDraftClaimMutation,
    invalidateDraftClaimQueries,
  }
}
